<template>
  <a-modal
    v-model:visible="visible"
    title="collection 列表"
    @ok="handleOk"
    @cancel="handleCancel"
    width="900px"
  >
    <template v-if="type == 'edit'">
      <div class="mb20" style="display:flex;justify-content: flex-start">
        <div
          style="display:flex;justify-content: flex-start;align-items: center;"
          class="mr20"
        >
          <label style="width:40px">ID:</label>
          <a-input
            v-model:value="colId"
            :maxLength="10"
            class="w160"
            placeholder="请输入"
          />
        </div>
        <div
          style="display:flex;justify-content: flex-start;align-items: center;"
          class="mr20"
        >
          <label class="w100">collection名称:</label>
          <a-input
            v-model:value="name"
            :maxLength="255"
            class="w160"
            placeholder="请输入"
          />
        </div>
        <a-button type="primary" @click="getCollection">查询</a-button>
        <a-button type="primary" style="margin-left: 10px;" @click="reset"
          >重置</a-button
        >
      </div>
      <a-table
        :columns="columns"
        :data-source="tableList"
        rowKey="collectId"
        :pagination="pagination"
        :row-selection="{
          selectedRowKeys: selectedKeys,
          onChange: onSelectChange
        }"
        @change="handleChangeTable"
      >
      </a-table>
    </template>
    <a-table
      v-else
      :columns="selectedColumns"
      :data-source="selectedList"
      rowKey="collectId"
      :pagination="false"
    />
  </a-modal>
</template>

<script setup>
import { ref, reactive, computed, defineEmits, defineProps, watch } from 'vue'
import { collection_list_post } from '@/server/modules/635'

// Props 定义
const props = defineProps({
  uniqueId: {
    type: [String, Number],
    required: true
  },
  show: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'edit'
  },
  collectionIds: {
    type: Array,
    default: () => []
  }
})

const uniq = computed(() => props.uniqueId)

// Emits 定义
const emit = defineEmits(['success'])

// 响应式状态
const colId = ref(null)
const name = ref(null)
const tableList = ref([])
const selectedKeys = ref([])
const visible = ref(props.show)

// 分页配置
const pagination = reactive({
  showTotal: total => `合计${total}条`,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: ['25', '50', '100'],
  current: 1,
  pageSize: 25,
  total: 0
})

watch(
  () => props.show,
  val => {
    visible.value = val
    val && getCollection()
  }
)
watch(
  () => visible.value,
  val => {
    emit('update:show', val)
  }
)

// 表格列定义
const columns = [
  {
    title: '操作',
    dataIndex: 'action',
    key: 'action',
    slots: { title: '操作' }
  },
  {
    title: 'ID',
    dataIndex: 'collectId',
    key: 'collectId'
  },
  {
    title: 'collection 名称',
    dataIndex: 'name',
    key: 'name'
  }
]

// 已选列表的列定义（不包含操作列）
const selectedColumns = [
  {
    title: 'ID',
    dataIndex: 'collectId',
    key: 'collectId'
  },
  {
    title: 'collection 名称',
    dataIndex: 'name',
    key: 'name'
  }
]

// 计算已选列表数据
// 存储所有已选中的数据项
const allSelectedItems = ref([])

// 修改计算属性，从 allSelectedItems 中获取数据
const selectedList = ref([])

watch(
  () => props.collectionIds,
  val => {
    allSelectedItems.value = val.map(id => ({ collectId: id, name: null }))
    selectedList.value = val.map(id => ({ collectId: id, name: null }))
  },
  { immediate: true, once: true }
)

// 修改选择变更处理函数
const onSelectChange = keys => {
  // 更新当前页面的选中状态
  selectedKeys.value = keys

  // 更新总的选中数据
  // 先移除当前页面所有数据
  const currentPageIds = tableList.value.map(item => item.collectId)
  allSelectedItems.value = allSelectedItems.value.filter(
    item => !currentPageIds.includes(item.collectId)
  )

  // 添加当前选中的数据
  const selectedRows = tableList.value.filter(item =>
    keys.includes(item.collectId)
  )
  allSelectedItems.value = [...allSelectedItems.value, ...selectedRows]
}

// 修改重置函数，清空所有选中状态
const reset = () => {
  colId.value = null
  name.value = null
  pagination.current = 1
  selectedKeys.value = [] // 重置当前页选中状态
  allSelectedItems.value = [] // 重置所有选中数据
  getCollection()
}

// 修改确认函数，使用所有选中数据的 ID
const handleOk = () => {
  visible.value = false
  emit(
    'updateCollectionIds',
    allSelectedItems.value.map(item => item.collectId)
  )
}

// 获取数据后，更新当前页的选中状态
const getCollection = async () => {
  const params = {
    pageNum: pagination.current,
    pageSize: pagination.pageSize,
    uniqueId: uniq.value,
    collectionId: colId.value,
    collectionName: name.value
  }
  const resData = await collection_list_post(params)
  if (resData.success && resData.data) {
    pagination.total = resData.data.total
    tableList.value = resData.data.list

    // 更新当前页的选中状态
    const currentPageSelectedIds = tableList.value
      .filter(item =>
        allSelectedItems.value.some(
          selected => selected.collectId === item.collectId
        )
      )
      .map(item => item.collectId)
    selectedKeys.value = currentPageSelectedIds
  }
}

const handleChangeTable = e => {
  pagination.current = e.current
  pagination.pageSize = e.pageSize
  getCollection()
}

// 初始化
getCollection()
</script>
